import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { CookieConsent } from '@trential/trential-ui-components';
import type { CookiePreferences } from '@global/types/cookie-consent';
import {
  getCookiePreferences,
  saveCookiePreferences,
  markPreferencesForSync,
  getPendingSyncPreferences,
  clearSyncFlag
} from '@global/helpers/cookie';
import { RootState } from '@redux/store';
import { saveUserConsent } from '@api/consent';
import { CookieConsentContext } from './context';
import useAnalyticsConsent from '@components/provider/hooks/use-analytics-consent';

interface CookieConsentProviderProps {
  children: React.ReactNode;
}

export default function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  const [preferences, setPreferences] = useState<CookiePreferences | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const isOgMode =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('og') === 'true';

  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const isAuthenticated = Boolean(accessToken);

  useEffect(() => {
    if (isOgMode) {
      setShowPopup(false);
      return;
    }

    const existingPrefs = getCookiePreferences();

    if (existingPrefs) {
      setPreferences(existingPrefs);
      setShowPopup(false);
    } else {
      setShowPopup(true);
    }
  }, [isOgMode]);

  // Sync pending preferences when user logs in
  useEffect(() => {
    const saveCookiePrefs = async () => {
      const pendingPrefs = getPendingSyncPreferences();

      if (!pendingPrefs) {
        return;
      }

      try {
        const resp = await saveUserConsent({
          consentType: 'cookie',
          consentPayload: {
            cookiesConsent: [pendingPrefs]
          }
        });
        if (resp.success) {
          clearSyncFlag();
        }
      } catch (error) {
        console.error('Error syncing cookie preferences:', error);
      }
    };

    if (isAuthenticated) {
      saveCookiePrefs();
    }
  }, [isAuthenticated]);

  const handleSavePreferences = useCallback(
    async (prefs: CookiePreferences) => {
      saveCookiePreferences(prefs);
      setPreferences(prefs);
      setShowPopup(false);

      // Sync to backend if authenticated, otherwise mark for later sync
      if (isAuthenticated) {
        try {
          await saveUserConsent({
            consentType: 'cookie',
            consentPayload: {
              cookiesConsent: [prefs]
            }
          });
        } catch (error) {
          console.error('Error saving cookie preferences to backend:', error);
        }
      } else {
        markPreferencesForSync();
      }
    },
    [isAuthenticated]
  );

  const hasConsent = useCallback(
    (category: keyof CookiePreferences): boolean => {
      if (!preferences) return false;

      return preferences[category] === true;
    },
    [preferences]
  );

  const showConsentDialog = useCallback(() => {
    setShowPopup(true);
  }, []);

  // initialize analytics related tools based on consent cookie
  useAnalyticsConsent({ hasConsent });

  const contextValue = {
    prefs: preferences,
    hasConsent,
    updatePreferences: handleSavePreferences,
    showConsentDialog
  };

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}

      <CookieConsent
        visible={showPopup && !isOgMode}
        position="bottom-left"
        onSavePreferences={handleSavePreferences}
      />
    </CookieConsentContext.Provider>
  );
}
